/**
 * Stills of a call's video, and saving them as picture files
 */

import logger from '@/utilities/Logger'

/** How long a saved file's object URL is kept - the download must have read it by then */
const URL_LIFETIME_MS = 60_000

/**
 * A still of the frame a video element shows now, at the video's own size
 * @param video The element
 * @returns The still on a canvas of its own, undefined while the video has no frame
 */
export function captureFrame(video: HTMLVideoElement): HTMLCanvasElement | undefined {
	const width = video.videoWidth
	const height = video.videoHeight

	if (!width || !height) { return undefined }

	const canvas = document.createElement('canvas')
	const ctx = canvas.getContext('2d')

	if (!ctx) { return undefined }
	canvas.width = width
	canvas.height = height
	ctx.drawImage(video, 0, 0, width, height)
	return canvas
}

/**
 * A canvas as a PNG file
 * @param canvas The canvas
 */
export function canvasToPng(canvas: HTMLCanvasElement): Promise<Blob> {
	return new Promise((resolve, reject) => {
		canvas.toBlob(blob => {
			if (blob) {
				resolve(blob)
			} else {
				reject(new Error('the canvas gave no picture'))
			}
		}, 'image/png')
	})
}

/**
 * Save a canvas as a PNG file - the browser's download, the Communicator Desktop app's save dialog. The download starts
 * in the canvas's own window, the one the user clicked in: the app's save dialog opens on it, and a browser counts
 * repeated downloads per window.
 * @param canvas The canvas
 * @param filename The file's name
 * @returns Whether the file was handed over to be saved
 */
export async function saveCanvas(canvas: HTMLCanvasElement, filename: string): Promise<boolean> {
	try {
		const doc = canvas.ownerDocument
		const url = URL.createObjectURL(await canvasToPng(canvas))
		const link = doc.createElement('a')

		link.href = url
		link.download = filename
		doc.body.appendChild(link)
		link.click()
		doc.body.removeChild(link)
		setTimeout(() => { URL.revokeObjectURL(url) }, URL_LIFETIME_MS)
		logger.info(`Snapshot: Saving ${filename} (${canvas.width}x${canvas.height})`)
		return true
	} catch (err) {
		logger.warning(`Snapshot: Could not save ${filename} - ${(err as Error).message}`)
		return false
	}
}
